import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./client";

// The API call budget (CONTRIBUTING.md, "API call budget"): a screen asks once, then nothing while the player
// looks at it. Nothing polls and nothing refetches on its own: data is fresh until an action changes it (the
// mutation updates or invalidates what it touched), its own time comes (a reveal, an auction's end), the app
// comes back to the foreground after a while (./freshness), or the player pulls to refresh.

/** Other players' things (auctions, profiles): fetched when shown, kept in memory only, and asked again when
 * shown after this long. */
export const SHOWN_MS = 30_000;

/** What everyone changes and the player only glances at (the league, the discovery feed): fresh this long, then
 * read again when shown or on the foreground. */
export const OTHERS_MS = 15 * 60_000;

let active: QueryClient | null = null;

/** The app's query client, for the modules that keep data outside a component (./ownCards, ./home). */
export const activeQueryClient = () => active;

export function createQueryClient() {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        // Fresh for the session unless a hook says otherwise: invalidation, not time, makes data stale.
        staleTime: Infinity,
        // Retry network hiccups, never a client error: a 4xx won't fix itself.
        retry: (failures, error) => !(error instanceof ApiError && error.status < 500) && failures < 2,
        // The foreground refresh is one rate-limited sweep (./freshness), not every query on every focus.
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      mutations: { retry: false },
    },
  });
  active = client;
  // Lazy: the listeners are not needed for the first paint.
  if (import.meta.env.MODE !== "test")
    void import("./freshness").then((m) => {
      m.installFreshness(client);
    });
  return client;
}
